import { createContext, useContext } from "react";

/**
 * Toast state and the non-React sink, kept apart from the components so
 * toast.tsx exports components only and keeps Vite's fast refresh.
 */
export type Toast = { id: number; text: string; tone: "error" | "info" };

export type ToastApi = {
  toasts: Toast[];
  push: (text: string, tone?: Toast["tone"]) => void;
  dismiss: (id: number) => void;
};

export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("ToastProvider not mounted");
  return ctx;
}

/**
 * Lets code outside the React tree raise a toast — specifically the
 * react-query MutationCache in main.tsx, which is constructed before any
 * component exists. ToastBridge registers the sink on mount.
 */
let sink: ((text: string, tone?: Toast["tone"]) => void) | null = null;

export function setToastSink(fn: ((text: string, tone?: Toast["tone"]) => void) | null) {
  sink = fn;
}

export function emitToast(text: string, tone: Toast["tone"] = "error") {
  sink?.(text, tone);
}
